import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { withTranslation } from 'react-i18next';

import { makeGetProject } from '../selectors/projects';
import { makeGetSortedActiveProjectMilestones, makeGetSortedArchivedProjectMilestones } from '../selectors/milestones';
import { makeGetFetchedState, makeGetLoadingState } from '../selectors/global';

import { fetchMilestones, updateMilestone, removeMilestone, setMilestoneVisibility, untieMilestoneTasks } from '../actions/milestones';
import { showModal } from '../actions/modal';

import { trackEvent } from '../services/events-tracker-service';
import { getMilestonesEnd } from '../utils/milestone';

import MilestoneListItem from './milestone-list-item';
import ProjectGanttSummary from './project-gantt-summary';
import CanUser from './can-user';
import NoEntries from './no-entries';

import GanttSideEntryPlaceholder from './placeholders/GanttSideEntry.placeholder';

class GanttChartSideProject extends Component {
    static defaultProps = {
        withHead: true
    };

    handleToggleDetailedViewClick = () => {
        this.handleExpandToggle();
        // this.props.onToggleDetailedViewClick(this.props.id);
    }

    attemptFetch() {
        if (this.props.placeholder || this.props.fetched || this.props.loading || !this.props.id) {
            return;
        }

        fetchMilestones(this.props.id);
    }

    handleMilestoneChange = (milestoneId, milestoneProps) => {
        this.updateMilestone(milestoneId, milestoneProps);

        setMilestoneVisibility(milestoneId, !milestoneProps.hiddenFromClient);
    }

    handleMilestoneDelete = milestoneId => {
        this.deleteMilestone(milestoneId);
    }

    handleMilestoneClick = milestoneId => {
        this.props.onMilestoneClick && this.props.onMilestoneClick(milestoneId);
    }

    handleSettingsClick = () => {
        this.props.history.push({
            pathname: `/settings/project/${this.props.id}`,
            state: {
                closeUrl: this.props.location.pathname
            }
        });

        this.props.close();
    }

    handleExpandToggle = () => {
        if (this.props.expanded) {
            this.props.onCollapse();
        } else {
            this.props.onExpand(this.props.id);
        }
    }

    handleAddMilestoneClick = () => {
        showModal('MILESTONE_CREATE', {
            projectId: this.props.id,
            milestones: this.props.milestones
        });
    }

    async updateMilestone(milestoneId, milestoneProps) {
        if (milestoneProps.end) {
            const milestone = this.props.milestones.find(milestone => milestone.id === milestoneId);

            this.updateDependentMilestones(milestoneId, milestoneProps.end - milestone.end);
        }

        await this.updateMilestoneTasks(milestoneId, milestoneProps);

        updateMilestone(milestoneId, milestoneProps);
    }

    updateMilestoneTasks(milestoneId, milestoneProps) {
        const originalProps = this.props.milestones.find(milestone => milestone.id === milestoneId);

        if (originalProps && originalProps.tasks && milestoneProps.tasks && originalProps.tasks.length !== milestoneProps.tasks.length) {
            untieMilestoneTasks(milestoneId, originalProps.tasks.filter(taskId => !milestoneProps.tasks.includes(taskId)));
        }
    }

    updateDependentMilestones(milestoneId, diff) {
        if (!diff) {
            return;
        }

        this.props.milestones
            .forEach(milestone => {
                if (milestone.dependency === milestoneId) {
                    this.updateMilestone(milestone.id, {
                        start: new Date(milestone.start).getTime() + diff,
                        end: new Date(milestone.end).getTime() + diff
                    });
                }
            });
    }

    deleteMilestone(milestoneId) {
        trackEvent('MILESTONE.REMOVE');

        removeMilestone(milestoneId);
    }

    get projectEnd() {
        return getMilestonesEnd(this.props.milestones);
    }

    get progress() {
        return Math.round(this.props.progress * 100);
    }

    get type() {
        if (!this.progress) {
            return 'default';
        }

        if (this.progress === 100) {
            return 'completed';
        }

        return 'in-progress'
    }

    get hasMilestones() {
        return this.props.milestones.length
    }

    shouldComponentUpdate(nextProps) {
        if (
            (this.props.id !== nextProps.id) ||
            (this.props.chartStartDate !== nextProps.chartStartDate) ||
            (this.props.dayWidth !== nextProps.dayWidth) ||
            (this.props.initials !== nextProps.initials) ||
            (this.props.milestones !== nextProps.milestones) ||
            (this.props.name !== nextProps.name) ||
            (this.props.placeholder !== nextProps.placeholder) ||
            (this.props.progress !== nextProps.progress) ||
            (this.props.projectEnd !== nextProps.projectEnd) ||
            (this.props.type !== nextProps.type) ||
            (this.props.selectedMilestone !== nextProps.selectedMilestone) ||
            (this.props.hoveredMilestone !== nextProps.hoveredMilestone) ||
            (this.props.expanded !== nextProps.expanded) ||
            (this.props.expandedMilestones !== nextProps.expandedMilestones) ||
            (this.props.sideEntryLayout !== nextProps.sideEntryLayout) ||
            (this.props.rowHeightMap !== nextProps.rowHeightMap) ||
            (this.props.compactView !== nextProps.compactView)
        ) return true;

        return false;
    }

    componentDidMount() {
        this.attemptFetch();
    }

    componentDidUpdate() {
        this.attemptFetch();
    }

    render() {
        if (this.props.placeholder) {
            return (
                <div className={'project-gantt-side-preview placeholder ' + this.type + (this.props.withMilestoneProgress ? ' with-milestone-progress': '')}>
                    <ProjectGanttSummary
                        project={{
                            initials: this.props.initials,
                            name: this.props.name,
                            color: 'rgb(196, 74, 228)'
                        }}
                        hasMilestones={true}
                        progress={this.props.progress}
                        type={this.props.type}
                        projectEnd={this.props.projectEnd}
                    />

                    <div className="preview-body">
                        {
                            this.props.milestones.map(milestone => <MilestoneListItem
                                key={milestone.id}
                                name={milestone.name}
                                description={milestone.description}
                                placeholder={true}
                                start={milestone.start}
                                end={milestone.end}
                                chartStartDate={this.props.chartStartDate}
                                dayWidth={this.props.dayWidth}
                            />)
                        }
                    </div>
                </div>
            )
        }

        const { project, t } = this.props;

        if (!this.hasMilestones && this.props.showEmpty) {
            return <NoEntries absolute={true} text={ t('noEntries.description') } />;
        }

        return (
            <div className={'project-gantt-side-preview ' + this.type + (this.props.withMilestoneProgress ? ' with-milestone-progress': '')}>
                {
                    this.props.withHead ? (
                        <ProjectGanttSummary
                            project={project}
                            hasMilestones={this.hasMilestones}
                            milestonesCount={this.props.milestones.length}
                            progress={this.progress}
                            type={this.type}
                            expanded={this.props.expanded}
                            compactView={this.props.compactView}
                            rowHeight={this.props.rowHeight}
                            projectEnd={this.projectEnd}
                            onClick={this.handleToggleDetailedViewClick}
                            onExpand={this.handleExpandToggle}
                        />
                    ) : null
                }

                {
                    this.props.expanded ? (
                        <div className="preview-body">
                            {
                                 this.hasMilestones ? this.props.milestones.map((milestone, index) => <MilestoneListItem
                                    key={milestone.id}
                                    id={milestone.id}
                                    index={index}
                                    projectId={this.props.id}
                                    name={milestone.name}
                                    description={milestone.description}
                                    placeholder={!this.props.milestones.length}
                                    milestones={this.props.milestones}
                                    publicPage={this.props.public}
                                    start={milestone.start}
                                    end={milestone.end}
                                    hidden={milestone.hidden}
                                    rule={milestone.rule}
                                    dependency={milestone.dependency}
                                    chartStartDate={this.props.chartStartDate}
                                    dayWidth={this.props.dayWidth}
                                    height={this.props.rowHeightMap && this.props.rowHeightMap[milestone.id]}
                                    selected={this.props.selectedMilestone === milestone.id || this.props.hoveredMilestone === milestone.id}
                                    expanded={this.props.expandedMilestones?.includes(milestone.id)}
                                    layout={this.props.sideEntryLayout}
                                    compactView={this.props.compactView}
                                    rowHeight={this.props.rowHeight}
                                    subrowHeight={this.props.subrowHeight}
                                    onChange={this.handleMilestoneChange}
                                    onDelete={this.handleMilestoneDelete}
                                    onClick={this.handleMilestoneClick}
                                    onMouseEnter={this.props.onMilestoneMouseEnter}
                                    onMouseLeave={this.props.onMilestoneMouseLeave}
                                    onExpand={this.props.onMilestoneExpand}
                                    onCollapse={this.props.onMilestoneCollapse}
                                    onTaskClick={this.props.onTaskClick}
                                />) :
                                this.props.showPlaceholder ? (
                                    <React.Fragment>
                                        <GanttSideEntryPlaceholder static={true} />

                                        <GanttSideEntryPlaceholder static={true} />

                                        <GanttSideEntryPlaceholder static={true} />
                                    </React.Fragment>
                                ) : null
                            }
                        </div>
                    ) : null
                }
            </div>
        )
    }
}

const makeMapStateToProps = () => {
    const getProject = makeGetProject();
    const getSortedActiveProjectMilestones = makeGetSortedActiveProjectMilestones();
    const getSortedArchivedProjectMilestones = makeGetSortedArchivedProjectMilestones();
    const getMilestonesFetched = makeGetFetchedState();
    const getMilestonesLoading = makeGetLoadingState();

    return ({ projects, milestones }, ownProps) => {
        if (ownProps.placeholder) {
            return {};
        }

        const project = getProject(projects.items, ownProps.id);

        return {
            project,
            milestones: ownProps.showArchived ? getSortedArchivedProjectMilestones(milestones.items, ownProps.id) : getSortedActiveProjectMilestones(milestones.items, ownProps.id),
            loading: getMilestonesLoading(milestones.fetchInfo, 'project', ownProps.id),
            fetched: getMilestonesFetched(milestones.fetchInfo, 'project', ownProps.id)
        }
    }
}

export default withRouter(connect(makeMapStateToProps)(withTranslation('milestone')(GanttChartSideProject)));
